import React, { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from "react";
import { IGenericRequestParameters, InferSortByOptions, StringIndexedDict } from "../../../api/GenericTypes";
import { Modal } from "../../../ELN/common/ELNModal/ELNModal";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { GenericEntityTable } from "./GenericEntityTable";
import { GenericEntitySelectWidget } from "./GenericEntitySelectWdiget";
import { useUnpaginateOrdered } from "../../../api/BaseEntityApi";
import { useForm } from "react-hook-form";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { NotAllowed } from "../../../common/misc/UIconstants";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";

export interface GenericEntitySelectModalProps<Entity extends GenericEntity & StringIndexedDict> {
  triggerComponent: ({ setIsOpen }: { setIsOpen: Dispatch<SetStateAction<boolean>> }) => JSX.Element;
  selectionCallback: (entity: Set<Entity["id"]>) => void;
  btnLabel?: ReactNode | string;
}

export const GenericEntitySelectModal = <
  Entity extends GenericEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntitySelectModalProps<Entity>>) => {
  const { entityConstants } = genericEntityConstants[entityTypeId];

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selection, setSelection] = useState<Set<Entity["id"]>>(new Set());

  const { data: entities } = useUnpaginateOrdered(
    entityConstants.resource,
    { ids: Array.from(selection) },
    { enabled: !!selection.size }
  );

  const { setValue, control } = useForm<{ genericEntitySelectedEntities: Entity[] }>({});

  useEffect(() => {
    if (!!entities) setValue("genericEntitySelectedEntities", entities as any);
    else setValue("genericEntitySelectedEntities", []);
  }, [entities, entityTypeId, setValue]);
  useEffect(() => {
    setSelection(new Set());
  }, [isOpen]);

  const onSubmitSelection = useCallback(() => {
    componentProps?.selectionCallback(selection);
    setIsOpen(false);
  }, [componentProps, selection]);

  const { canReadIndex } = useEntityPermissions({ entityTypeId });
  if (!canReadIndex) return NotAllowed;
  return (
    <>
      <div className="flex align-center gap-5" style={{ justifyContent: "space-between", paddingTop: "5px" }}>
        {componentProps?.triggerComponent?.({ setIsOpen }) ?? (
          <button className="btn btn-primary flex align-center gap-5" onClick={() => setIsOpen(true)}>
            <LucideIcon name="list-todo" />
            Select {entityConstants.entityPlural}
          </button>
        )}
      </div>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <Modal.Body>
          <div style={{ width: "80vw", height: "80vh" }}>
            <GenericEntityTable<Entity, Filters, StringIndexedDict>
              entityTypeId={entityTypeId}
              componentProps={{
                hideTabs: true,
                hideCustomTypedEntityAddButton: true,
                selectionCallback: setSelection,
                onRowClickPreset: "select",
                showPermissionColumn: false,
                hideHeaderControls: true,
                hideTableControls: true,
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Controls>
          <div style={{ width: "80vw", overflow: "hidden" }}>
            <div className="flex align-center gap-5" style={{ width: "100%", height: "100%" }}>
              <div style={{ width: "100%" }}>
                <GenericEntitySelectWidget<Entity, Entity, Filters>
                  entityTypeId={entityTypeId}
                  componentProps={{
                    id: "genericEntitySelectedEntities" as any,
                    style: { margin: 0, padding: 0, width: "100%" },
                    horizontal: true,
                    hasLabel: false,
                    disabled: true,
                    filters: { ids: Array.from(selection) } as any,
                    control: control as any,
                    isMulti: true,
                    formStyle: { margin: 0, padding: 0 },
                  }}
                />
              </div>
              <button className="btn btn-primary" onClick={onSubmitSelection}>
                {componentProps?.btnLabel ?? (
                  <>
                    Select {selection.size}{" "}
                    {selection.size !== 1 ? entityConstants.entityPlural : entityConstants.entitySingular}...
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal.Controls>
      </Modal>
    </>
  );
};
